import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useMemo,
  useRef,
  useState
} from "react";

import { CloseIcon, InformationIcon, SearchIcon } from "@/assets/icons";
import { MESSAGE } from "@/constants/message";
import { capitalizeEachWord } from "@/helpers/string";
import { cn, normalizeFilterValue } from "@/lib/utils";
import { DatasetDataType } from "@/types/dataset";

import Chip, { ChipAttribute } from "./chip";
import TooltipButton from "./TooltipButton";

type FilterOption = {
  label: string;
  value: string;
  code: string;
  type: DatasetDataType;
};

export type MultipleSearchFilterParams = {
  query: string[] | null;
  query_type: DatasetDataType[] | null;
  query_column: string[] | null;
};

interface MultipleSearchWithSelectProps<T extends MultipleSearchFilterParams> {
  searchPlaceholder?: string;
  optionPlaceholder?: string;
  options: FilterOption[];
  searchParams: T;
  disabled?: boolean;
  className?: string;
  onParamsChange: (params: MultipleSearchFilterParams) => void;
}

const MultipleSearchWithSelect = <T extends MultipleSearchFilterParams>({
  searchPlaceholder = "Search and filter",
  optionPlaceholder = "Loading attributes...",
  searchParams,
  options = [],
  disabled = false,
  onParamsChange,
  className
}: MultipleSearchWithSelectProps<T>) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const [selectedAttribute, setSelectedAttribute] = useState("");
  const [showAttributes, setShowAttributes] = useState(false);
  const [activeQuery, setActiveQuery] = useState("");
  const [editingChipId, setEditingChipId] = useState<string | null>(null);

  const availableOptions = useMemo(() => {
    const used = new Set(searchParams.query_column ?? []);
    return options.filter(({ code }) => !used.has(code));
  }, [options, searchParams.query_column]);

  const getLabelChip = (selectedAttribute: string) => {
    const option = options.find((opt) => opt.code === selectedAttribute);
    return option ? capitalizeEachWord(option.value, "_") : "";
  };

  const chips: ChipAttribute[] =
    searchParams.query?.map((value, index) => ({
      id: `${searchParams.query_column?.[index] || ""}-${value}-${index}`,
      key: searchParams.query_column?.[index] || "",
      label: getLabelChip(searchParams.query_column?.[index] || ""),
      value
    })) || [];

  const emitParamsChange = useCallback(
    (updatedChips: ChipAttribute[]) => {
      if (updatedChips.length === 0) {
        onParamsChange({
          query: null,
          query_type: null,
          query_column: null
        });
        return;
      }

      onParamsChange({
        query: updatedChips.map((c) => c.value),
        query_column: updatedChips.map((c) => normalizeFilterValue(c.key)),
        query_type: updatedChips.map(
          (c) =>
            availableOptions.find(
              (opt) => opt.value === normalizeFilterValue(c.key)
            )?.type ?? DatasetDataType.TYPE_STRING
        )
      });
    },
    [onParamsChange, availableOptions]
  );

  const updateChipValue = (id: string, value: string) => {
    const updated = chips.map((chip) =>
      chip.id === id ? { ...chip, value } : chip
    );
    emitParamsChange(updated);
  };

  const addChip = (key: string, value: string, label: string) => {
    const newChip: ChipAttribute = {
      id: `${key}-${value}-${Date.now()}`,
      key: key.replace(/:$/, ""),
      label,
      value
    };
    emitParamsChange([...chips, newChip]);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) =>
    setActiveQuery(e.target.value);

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const value = e.currentTarget.value.trim();

    if (e.key === "Enter" && value) {
      e.preventDefault();

      if (selectedAttribute) {
        addChip(selectedAttribute, value, getLabelChip(selectedAttribute));
      }

      setEditingChipId(null);
      setSelectedAttribute("");
      setActiveQuery("");
      setShowAttributes(false);

      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft =
            scrollContainerRef.current.scrollWidth;
        }
        inputRef.current?.focus();
      }, 0);
    }

    if (e.key === "Backspace" && value === "") {
      e.preventDefault();
      setSelectedAttribute("");
      setEditingChipId(null);
      setShowAttributes(true);
    }
  };

  const handleOptionSelect = (value: string, code: string) => {
    const item = availableOptions.find(
      (opt) => opt.value === value && opt.code === code
    );
    if (item) {
      setSelectedAttribute(item.code);
      setShowAttributes(false);
      inputRef.current?.focus();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLFieldSetElement>) => {
    const nextTarget = e.relatedTarget;
    const value = activeQuery.trim();

    if (nextTarget && e.currentTarget.contains(nextTarget)) return;

    if (selectedAttribute && value) {
      if (editingChipId) {
        updateChipValue(editingChipId, value);
        setEditingChipId(null);
      }
      addChip(selectedAttribute, value, getLabelChip(selectedAttribute));
    }

    setActiveQuery("");
    setSelectedAttribute("");

    hideTimeout.current = setTimeout(() => setShowAttributes(false), 150);
  };

  const handleChipEdit = (chip: ChipAttribute) => {
    const remainingChips = chips.filter((c) => c.id !== chip.id);
    emitParamsChange(remainingChips);

    setEditingChipId(chip.id);
    setSelectedAttribute(chip.key);
    setActiveQuery(chip.value);

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(
          inputRef.current.value.length,
          inputRef.current.value.length
        );
      }
    }, 0);
  };

  const handleClearAll = () => {
    setActiveQuery("");
    setSelectedAttribute("");
    setShowAttributes(false);
    emitParamsChange([]);
  };

  const hasContent = chips.length > 0 || activeQuery.length > 0;
  const readOnly = disabled || (!selectedAttribute && !editingChipId);
  const placeholder =
    !hasContent && !selectedAttribute && !editingChipId
      ? searchPlaceholder
      : "";

  const renderDropdownContent = () => {
    const hasQueryColumns = (searchParams.query_column?.length ?? 0) > 0;
    const allOptionsUsed = availableOptions.length === 0 && hasQueryColumns;
    if (availableOptions.length === 0 && !hasQueryColumns) {
      return (
        <div className="p-3 text-sm text-gray-500">{optionPlaceholder}</div>
      );
    }
    if (allOptionsUsed) {
      return (
        <div className="p-3 text-sm text-gray-500">
          All attributes are already used
        </div>
      );
    }

    return availableOptions.map((item) => (
      <button
        key={item.value}
        type="button"
        tabIndex={0}
        onClick={() => handleOptionSelect(item.value, item.code)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ")
            handleOptionSelect(item.value, item.code);
        }}
        className="flex items-center w-full gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100 first:rounded-t-xl last:rounded-b-xl"
      >
        <SearchIcon className="flex-shrink-0 w-4 h-4 fill-gray-700" />
        {capitalizeEachWord(item.label, "_")}
      </button>
    ));
  };

  return (
    <div
      className={cn(
        "flex items-center gap-3 text-sm justify-between relative break-all",
        !disabled && "z-[2000]",
        className
      )}
    >
      <fieldset
        className="relative flex items-center w-full gap-2"
        aria-label="filter and search group container"
        onBlur={handleBlur}
      >
        <div
          onClick={() => {
            if (disabled || selectedAttribute) return;
            inputRef.current?.focus();
            setShowAttributes(true);
          }}
          className={cn(
            "flex w-full items-center gap-1 border rounded-[12px] pl-3 pr-1 h-[45px] transition-all",
            disabled
              ? "bg-neutral-50 border-neutral-200"
              : "bg-white border-neutral-200 focus-within:border-teal-500"
          )}
        >
          <SearchIcon className="fill-gainsboro w-5 h-5 mr-0.5 flex-shrink-0" />
          <div
            ref={scrollContainerRef}
            className="flex w-full transition-all items-center h-[45px] space-x-1 flex-grow min-w-0 overflow-x-auto scrollbar-overlay"
          >
            {chips.map((chip) => (
              <Chip
                key={chip.id}
                chip={chip}
                onDelete={(id) =>
                  emitParamsChange(chips.filter((c) => c.id !== id))
                }
                onEdit={handleChipEdit}
              />
            ))}

            {selectedAttribute && (
              <span className="flex-shrink-0 py-2 pl-1 text-xs font-bold text-gray-700">
                {getLabelChip(selectedAttribute) + ": "}
              </span>
            )}

            <input
              ref={inputRef}
              type="text"
              value={activeQuery}
              placeholder={placeholder}
              readOnly={readOnly}
              disabled={disabled}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              className="w-full text-sm bg-transparent outline-none cursor-pointer min-w-28"
              aria-label="Search and filter"
            />
          </div>

          {hasContent && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleClearAll();
              }}
              aria-label="Clear Multiple Search With Select"
              className="flex items-center p-2 text-neutral-500 hover:text-neutral-900"
            >
              <CloseIcon className="w-5 h-5 stroke-2" />
            </button>
          )}
        </div>
        {!disabled && showAttributes && (
          <div
            aria-label="filter options"
            className="absolute left-0 top-full mt-1 w-full max-h-[260px] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg max-w-[calc(100vw-148px)]"
          >
            {renderDropdownContent()}
          </div>
        )}
        <TooltipButton
          className="h-fit"
          arrowClassName="mr-1.5"
          position="top-right"
          content={MESSAGE.MULTIPLE_SEARCH_WITH_SELECT_INFO}
          ariaLabel="filter and search info tooltip"
        >
          <InformationIcon className="w-6 h-6 fill-gray-700" />
        </TooltipButton>
      </fieldset>
    </div>
  );
};

export default MultipleSearchWithSelect;
